import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@shared/components/ui/card";
import { cn } from "@shared/utils/cn";
import type { AvailableArea } from "@features/booking/types/timeSlots";

interface AreaListProps {
  areas: AvailableArea[];
  onSelect: (areaId: number) => void;
  selectedArea: string | null;
}

const AreaList = ({ areas, onSelect, selectedArea }: AreaListProps) => {
  const { t } = useTranslation('booking');

  return (
    <div className="w-full space-y-4">
      {areas.map((area) => {
        const isSelected = selectedArea === String(area.id);
        const disabled = !area.available;
        const handleActivate = () => {
          if (!disabled) onSelect(area.id);
        };
        return (
          <Card
            key={area.id}
            className={cn(
              "overflow-hidden border-2 transition-colors",
              isSelected ? "border-brand" : "border-border",
              disabled
                ? "opacity-60 cursor-not-allowed"
                : "cursor-pointer hover:border-brand-light",
            )}
            role="button"
            tabIndex={0}
            aria-disabled={disabled}
            aria-pressed={isSelected}
            onClick={handleActivate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate();
              }
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-medium text-foreground mb-1">
                    {area.name}
                  </h3>
                  {area.description && (
                    <p className="text-foreground/60 text-sm">
                      {area.description}
                    </p>
                  )}
                  {disabled && (
                    <p className="text-foreground/50 text-sm font-medium mt-2">
                      {t('NOT_AVAILABLE')}
                    </p>
                  )}
                </div>
                {!disabled && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-foreground/60 shrink-0 mt-1"
                    width={20}
                    height={20}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" strokeWidth={2} />
                  </svg>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AreaList;
